<template>
  <v-dialog
    :value="value"
    content-class="crud-dialog"
    persistent
    max-width="520px"
    @input="$emit('input', $event)"
  >
    <v-card class="crud-card">
      <v-card-title class="crud-card-title">
        <v-row no-gutters>
          <v-col cols="11" class="text-left">
            <h2 class="text-h5">
              A collection is already booked for this address
            </h2>
          </v-col>
          <v-col cols="1">
            <v-icon class="close" color="black" @click.stop="close">{{
              icons.mdiClose
            }}</v-icon>
          </v-col>
        </v-row>
      </v-card-title>
      <v-divider />
      <v-card-text class="crud-card-text">
        <p>
          A Courier Guy collection is already scheduled for
          <strong>{{ address }}</strong>
          <template v-if="collectionDate">
            on <strong>{{ collectionDate }}</strong></template
          >. A second booking cannot be made for this address.
        </p>
        <p>
          Add the wills to that collection instead, and hand them all to the
          courier when they arrive.
        </p>

        <template v-if="mode === 'advisor'">
          <div v-if="$apollo.queries.eligibleTestators.loading" class="py-4">
            <v-progress-circular indeterminate size="24" color="primary" />
            <span class="pl-3">Loading the wills you hold...</span>
          </div>
          <template v-else-if="eligibleTestators.length">
            <p class="mb-1">
              <strong>Choose the wills to add to this collection</strong>
            </p>
            <div class="testator-list">
              <Checkbox
                v-for="testator in eligibleTestators"
                :key="testator.id"
                :value="isSelected(testator.id)"
                :label="testator.fullName || `Testator ${testator.id}`"
                color="primary"
                @input="toggle(testator.id, $event)"
              />
            </div>
          </template>
          <v-alert v-else type="info" text dense class="mb-0">
            None of the wills you hold are ready to be added: they must be
            validated, not yet vaulted, and not already booked.
          </v-alert>
        </template>
        <p v-else-if="testatorName" class="mb-0">
          <strong>{{ testatorName }}</strong
          >'s will can be added to this collection.
        </p>

        <v-alert v-if="errors.length" type="error" text dense class="mt-4 mb-0">
          <div v-for="(error, index) in errors" :key="index">{{ error }}</div>
        </v-alert>

        <div class="mt-8 mb-2 d-flex justify-end flex-wrap action-row">
          <GradientButton
            :title="'Change address'"
            :small="true"
            :fill="false"
            @gradientButtonClicked="close"
          />
          <GradientButton
            :title="linkButtonTitle"
            :small="true"
            :callout="'right'"
            :disabled="!canLink"
            :loading="linking"
            @gradientButtonClicked="link"
          />
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
  import { mdiClose } from '@mdi/js';
  import {
    ADVISOR_COLLECTION_ELIGIBLE_TESTATORS_QUERY,
    LINK_TESTATORS_TO_WILL_COLLECTION_BOOKING_MUTATION,
  } from '../../../constants/graphql/testators';

  // Shown to advisers (booking from the dashboard, or while impersonating a
  // testator) when the pickup address they entered already has a collection
  // booked and not yet picked up. A second booking is never allowed; instead
  // the adviser adds wills to the existing collection, and each added
  // testator then counts as "collection booked" everywhere.
  export default {
    name: 'ExistingCollectionBookingDialog',
    props: {
      value: {
        type: Boolean,
        default: false,
      },
      // The pendingWillCollectionBookingAtAddress / duplicateAddressBooking
      // payload: { id, address, collectionDate, expectedCollectionDate, collectionDetails }.
      booking: {
        type: Object,
        default: null,
      },
      // 'advisor': pick from the wills the adviser holds.
      // 'testator': add the one testator being booked for (impersonation).
      mode: {
        type: String,
        default: 'testator',
      },
      // Named to avoid the app-wide global-mixin computed `testatorId`, which
      // would collide with (and Vue would warn about) a same-named prop.
      bookingTestatorId: {
        type: [String, Number],
        default: null,
      },
      testatorName: {
        type: String,
        default: '',
      },
      // Advisor mode: wills the adviser had already ticked in the booking
      // form, so the dialog starts with the same selection.
      preselectedTestatorIds: {
        type: Array,
        default: () => [],
      },
    },
    data() {
      return {
        icons: { mdiClose },
        eligibleTestators: [],
        selectedIds: [],
        linking: false,
        errors: [],
      };
    },
    computed: {
      address() {
        return this.booking?.address || 'this address';
      },
      collectionDate() {
        if (!this.booking) return '';
        return (
          this.booking.collectionDetails || this.booking.collectionDate || ''
        );
      },
      testatorIdsToLink() {
        if (this.mode === 'advisor') return this.selectedIds;
        return this.bookingTestatorId ? [this.bookingTestatorId] : [];
      },
      canLink() {
        return (
          !!this.booking?.id &&
          this.testatorIdsToLink.length > 0 &&
          !this.linking
        );
      },
      linkButtonTitle() {
        if (this.mode !== 'advisor') return 'Add this will to the collection';
        const count = this.selectedIds.length;
        if (count <= 1) return 'Add to this collection';
        return `Add ${count} wills to this collection`;
      },
    },
    watch: {
      value(open) {
        if (open) {
          this.errors = [];
          this.selectedIds = this.preselectedTestatorIds.map(String);
        }
      },
      // Once the eligible list arrives, drop any preselected id that is no
      // longer eligible (booked meanwhile, will re-uploaded, and so on).
      eligibleTestators(list) {
        const eligibleIds = new Set(list.map(t => String(t.id)));
        this.selectedIds = this.selectedIds.filter(id => eligibleIds.has(id));
      },
    },
    methods: {
      isSelected(id) {
        return this.selectedIds.includes(String(id));
      },
      toggle(id, checked) {
        const key = String(id);
        if (checked && !this.selectedIds.includes(key)) {
          this.selectedIds = [...this.selectedIds, key];
        } else if (!checked) {
          this.selectedIds = this.selectedIds.filter(s => s !== key);
        }
      },
      close() {
        this.$emit('input', false);
      },
      async link() {
        if (!this.canLink) return;
        this.linking = true;
        this.errors = [];
        try {
          const response = await this.$apollo.mutate({
            mutation: LINK_TESTATORS_TO_WILL_COLLECTION_BOOKING_MUTATION,
            variables: {
              bookingId: this.booking.id,
              testatorIds: this.testatorIdsToLink.map(String),
            },
          });
          const payload =
            response?.data?.linkTestatorsToWillCollectionBooking || null;
          const linkedIds = (payload && payload.linkedTestatorIds) || [];
          const errors = (payload && payload.errors) || [];

          if (linkedIds.length) {
            this.$emit('linked', {
              linkedTestatorIds: linkedIds,
              errors,
              booking: this.booking,
            });
            this.close();
          } else {
            this.errors = errors.length
              ? errors
              : ['The wills could not be added to this collection.'];
          }
        } catch (error) {
          this.errors = [
            error.message || 'The wills could not be added to this collection.',
          ];
        } finally {
          this.linking = false;
        }
      },
    },
    apollo: {
      eligibleTestators: {
        query: ADVISOR_COLLECTION_ELIGIBLE_TESTATORS_QUERY,
        update: data => data.advisorCollectionEligibleTestators || [],
        skip() {
          return this.mode !== 'advisor' || !this.value;
        },
        fetchPolicy: 'network-only',
        error(error) {
          console.error('Error fetching wills ready for collection:', error);
        },
      },
    },
  };
</script>

<style scoped lang="scss">
  .testator-list {
    max-height: 260px;
    overflow-y: auto;
    padding: 4px 0;
  }

  .action-row {
    gap: 12px;
  }
</style>
